import {AttributesRow, openDialog, Tooltip} from 'platform/components';
import {HStack, Link} from 'platform/foundation';
import {formatPhoneNumber, useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {always, defaultTo, isNotNil} from 'ramda';
import {isFalsy, isNotNilOrEmpty, isString, isTruthy} from 'ramda-adjunct';

import {useGetTagsForEntityQuery} from '@dms/api/core';
import {CustomerResponseBodyV2} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {buildArray, EMPTY_PLACEHOLDER, Nullish, parseDate} from 'shared/web';

import {InvalidEmailWarning} from '../components/InvalidEmailWarning/InvalidEmailWarning';
import {SendCustomerEmailForm} from '../components/SendCustomerEmailForm/SendCustomerEmailForm';
import {SendCustomerSmsForm} from '../components/SendCustomerSmsForm/SendCustomerSmsForm';
import {ValueWithTags} from '../components/ValueWithTags/ValueWithTags';
import {getLocaleLabel} from '../utils/getLocaleLabel';
import {getNaturalPersonFullName} from '../utils/getNaturalPersonFullName';
import {getPreferredLanguageName} from '../utils/getPreferredLanguageName';
import {isCustomerNaturalPerson} from '../utils/isCustomerNaturalPerson';
import {isInvalidEmail} from '../utils/isInvalidEmail';
import {useAddress} from './useAddress';
import {useCountry} from './useCountry';
import {useCustomerGroups} from './useCustomerGroups';
import {useEmailConfig} from './useEmailConfig';
import {useGender} from './useGender';
import {useIdentityCard} from './useIdentityCard';
import {useInstitution} from './useInstitution';
import {useKonzultaSmsAvailability} from './useKonzultaSmsAvailability';
import {useTenant} from './useTenant';

export function useCustomerInformationAttributes(
  customer?: CustomerResponseBodyV2,
  displayAftersalesVersion?: boolean,
  resourceId?: string,
  recordId?: string
) {
  const formatDateTime = useDateTimeFormatter();

  const {composeAddress} = useAddress();
  const {getCountryName} = useCountry();
  const {composeIdentityCard} = useIdentityCard();
  const {getGenderName} = useGender();
  const {getInstitutions} = useInstitution();
  const {getCustomerGroupsAttribute} = useCustomerGroups();
  const {isTenantCountryRestricted, isMultiLocale} = useTenant();
  const {isEnabled: isEmailConfigEnabled} = useEmailConfig();
  const {isSmsActionDisabled, smsTooltipLabel} = useKonzultaSmsAvailability();

  const {data: tags} = useGetTagsForEntityQuery(
    {
      resourceId: EntityResourceIds.customer,
      recordId: customer?.id ?? '',
    },
    {skip: !displayAftersalesVersion || !customer}
  );

  if (!customer) {
    return;
  }

  const validRecordId = isValidResourceId(resourceId) && recordId ? recordId : customer.id;
  const validResourceId =
    isValidResourceId(resourceId) && recordId ? resourceId : EntityResourceIds.customer;

  const getCustomerLegalPerson = () =>
    buildArray<AttributesRow>()
      .add({
        label: i18n.t('contractInformation.labels.tradeName'),
        content: (
          <ValueWithTags value={customer.businessInfo?.businessInfo.tradeName} tags={tags} />
        ),
      })
      .add({
        label: i18n.t('contractInformation.labels.registrationNumber'),
        value: customer.businessInfo?.businessInfo.registrationNumber,
      })
      .add({
        label: i18n.t('contractInformation.labels.vatNumber'),
        value: customer.businessInfo?.businessInfo.vatNumber,
      })
      .add({
        label: i18n.t('contractInformation.labels.fileNumber'),
        value: customer.businessInfo?.businessInfo.fileNumber,
      })
      .add({
        label: i18n.t('contractInformation.labels.countryOfRegistrationCode'),
        value: getCountryName(customer.businessInfo?.businessInfo?.countryOfRegistrationCode),
      })
      .add({
        label: i18n.t('contractInformation.labels.businessAddressId'),
        value: composeAddress(customer.businessInfo?.address?.address),
      })
      .when(isMultiLocale && isNotNil(customer.preferredLocale), {
        label: i18n.t('entity.person.labels.locale'),
        value: getLocaleLabel(customer.preferredLocale),
      })
      .add({
        label: i18n.t('entity.businessInfo.labels.institutions.title'),
        value: getInstitutions(customer.institutions),
      })
      .add({
        label: i18n.t('entity.person.labels.customerGroups'),
        content: getCustomerGroupsAttribute(customer.customerGroups),
      });

  // eslint-disable-next-line complexity -- Pre-existing violation
  const getCustomerPhysicalPerson = () => {
    const foundingPerson = buildArray<AttributesRow>()
      .add({
        label: i18n.t('entity.person.labels.fullName'),
        content: (
          <ValueWithTags value={getNaturalPersonFullName(customer.foundingPerson)} tags={tags} />
        ),
      })
      .when(isString(customer.foundingPerson?.roles), {
        label: i18n.t('entity.person.labels.role'),
        value: customer.foundingPerson?.roles,
      })
      .when(isFalsy(displayAftersalesVersion), {
        label: i18n.t('entity.person.labels.genderKey'),
        value: getGenderName(customer.foundingPerson?.genderKey),
      })
      .when(isMultiLocale && isNotNil(customer.preferredLocale), {
        label: i18n.t('entity.person.labels.locale'),
        value: getLocaleLabel(customer.preferredLocale),
      })
      .when(isTenantCountryRestricted(['BEL']), {
        label: i18n.t('entity.person.labels.preferredLanguage'),
        value: getPreferredLanguageName(i18n, customer.foundingPerson?.preferredLanguage),
      })
      .when(isFalsy(displayAftersalesVersion), {
        label: i18n.t('entity.person.labels.citizenshipCode'),
        value: getCountryName(customer.foundingPerson?.citizenshipCode),
      })
      .add({
        label: i18n.t('entity.person.labels.birthdate'),
        value: isNotNilOrEmpty(customer.foundingPerson?.birthdate)
          ? formatDateTime('dateShort', parseDate(customer!.foundingPerson!.birthdate!))
          : null,
      })
      .add({
        label: i18n.t('entity.person.labels.personalIdentifier'),
        value: customer.foundingPerson?.personalIdentifier,
      })
      .add({
        label: i18n.t('entity.customer.labels.isSelfEmployed'),
        value: match(customer.selfEmployed)
          .with(true, always(i18n.t('general.labels.yes')))
          .with(false, always(i18n.t('general.labels.no')))
          .otherwise(always(null)),
      });

    if (isTruthy(customer.selfEmployed)) {
      foundingPerson
        .add({
          label: i18n.t('contractInformation.labels.tradeName'),
          value: customer?.selfEmployedBusinessInfoData?.tradeName,
        })
        .add({
          label: i18n.t('contractInformation.labels.registrationNumber'),
          value: customer?.selfEmployedBusinessInfoData?.registrationNumber,
        })
        .add({
          label: i18n.t('contractInformation.labels.vatNumber'),
          value: customer?.selfEmployedBusinessInfoData?.vatNumber,
        })
        .add({
          label: i18n.t('contractInformation.labels.fileNumber'),
          value: customer?.selfEmployedBusinessInfoData?.fileNumber,
        })
        .add({
          label: i18n.t('contractInformation.labels.countryOfRegistrationCode'),
          value: getCountryName(customer?.selfEmployedBusinessInfoData?.countryOfRegistrationCode),
        })
        .add({
          label: i18n.t('contractInformation.labels.businessAddressId'),
          value: composeAddress(customer?.selfEmployedBusinessAddress?.address),
        });
    }

    foundingPerson.whenNot(customer.foundingPerson?.phoneNumbers?.length, {
      label: i18n.t('entity.phoneNumber.labels.number'),
      value: null,
    });

    customer.foundingPerson?.phoneNumbers?.forEach((item) => {
      foundingPerson.when(isNotNil(customer.id), {
        label: `${i18n.t('entity.phoneNumber.labels.number')} ${
          isNotNilOrEmpty(item.type) ? `(${item.type})` : ''
        }`,
        content: (
          <Tooltip label={smsTooltipLabel} placement="top-start">
            <Link
              title={defaultTo(
                EMPTY_PLACEHOLDER,
                formatPhoneNumber(`${item.prefix}${item.number}`)
              )}
              size="xSmall"
              isDisabled={isSmsActionDisabled}
              onClick={() =>
                openDialog(
                  <SendCustomerSmsForm
                    customerId={customer.id}
                    recordId={validRecordId}
                    resourceId={validResourceId}
                  />,
                  {
                    scrollBehavior: 'outside',
                    size: 'large',
                    title: i18n.t('general.labels.smsMessage'),
                  }
                )
              }
              data-testid="send-sms-open-modal-link"
            />
          </Tooltip>
        ),
      });
    });

    foundingPerson.whenNot(customer.foundingPerson?.emails?.length, {
      label: i18n.t('entity.email.labels.email'),
      value: null,
    });

    customer.foundingPerson?.emails?.forEach((item) => {
      const sendEmailLink = (
        <Tooltip
          label={
            isEmailConfigEnabled
              ? i18n.t('general.actions.sendEmail')
              : i18n.t('general.notifications.setupEmailProvider')
          }
          placement="top-start"
        >
          <Link
            title={defaultTo(EMPTY_PLACEHOLDER, item.email)}
            size="xSmall"
            isDisabled={!isEmailConfigEnabled}
            onClick={() =>
              openDialog(
                <SendCustomerEmailForm
                  customerId={customer.id}
                  recordId={validRecordId}
                  resourceId={validResourceId}
                />,
                {
                  withAdditionalFooter: true,
                  size: 'large',
                  title: i18n.t('general.actions.sendEmail'),
                }
              )
            }
            data-testid="send-email-open-modal-link"
          />
        </Tooltip>
      );

      foundingPerson.when(isNotNil(customer.id), {
        label: `${i18n.t('entity.email.labels.email')} ${
          isNotNilOrEmpty(item.type) ? `(${item.type})` : ''
        }`,
        content: isInvalidEmail(item.email) ? (
          <HStack spacing={1} align="center">
            {sendEmailLink}
            <InvalidEmailWarning email={item.email} data-testid="customerInformation-email" />
          </HStack>
        ) : (
          sendEmailLink
        ),
      });
    });

    foundingPerson.add({
      label: `${i18n.t('entity.address.labels.fullAddress')}
      ${
        isNotNilOrEmpty(customer.foundingPerson?.permanentAddress?.type)
          ? `(${customer.foundingPerson!.permanentAddress!.type})`
          : ''
      }`,
      value: composeAddress(customer.foundingPerson?.permanentAddress?.address),
    });

    if (isFalsy(displayAftersalesVersion)) {
      foundingPerson.whenNot(customer.foundingPerson?.identityCards?.length, {
        label: i18n.t('entity.identityCard.labels.fullIdentityCard'),
        value: null,
      });

      customer.foundingPerson?.identityCards?.forEach((item) => {
        foundingPerson.add({
          label: i18n.t('entity.identityCard.labels.fullIdentityCard'),
          value: composeIdentityCard(item),
        });
      });
    }

    foundingPerson.add({
      label: i18n.t('entity.person.labels.customerGroups'),
      content: getCustomerGroupsAttribute(customer.customerGroups),
    });

    return foundingPerson;
  };

  return isCustomerNaturalPerson(customer) ? getCustomerPhysicalPerson() : getCustomerLegalPerson();
}

const isValidResourceId = (resourceId: string | Nullish): resourceId is EntityResourceIds =>
  isNotNilOrEmpty(resourceId) &&
  Object.values(EntityResourceIds).includes(resourceId as EntityResourceIds);
